import { useSearchParams } from "react-router-dom"

/**
 * Aba ativa guardada na query string (`?tab=`).
 *
 * A aba na URL é o que faz um link direto sobreviver ao F5 e ao envio para um
 * colega. O padrão estava reimplementado em cada tela com abas, e as cópias já
 * divergiam em detalhes que o usuário sente: uma delas trocava o objeto inteiro
 * de parâmetros ao mudar de aba e derrubava os params irmãos junto (filtro,
 * contexto de origem). Este hook fixa o comportamento correto num lugar só.
 *
 * Regras:
 * - valor desconhecido na URL cai na aba padrão, nunca em tela sem aba ativa
 *   (link antigo, typo, aba que foi removida);
 * - a aba padrão fica SEM o parâmetro, para que a URL limpa seja a canônica;
 * - os demais parâmetros da URL são preservados;
 * - a troca usa `replace`, porque mudar de aba não é navegação: o voltar do
 *   browser deve sair da tela, não desfazer cliques em aba.
 *
 * @param values lista fechada de abas válidas
 * @param defaultTab aba usada quando a URL não traz nenhuma válida
 * @param param nome do parâmetro na query string
 */
export const useUrlTab = <T extends string>(
  values: readonly T[],
  defaultTab: T,
  param = "tab"
): [T, (next: string) => void] => {
  const [searchParams, setSearchParams] = useSearchParams()

  const requested = searchParams.get(param)
  const activeTab = values.includes(requested as T)
    ? (requested as T)
    : defaultTab

  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams)
    if (next === defaultTab) {
      params.delete(param)
    } else {
      params.set(param, next)
    }
    setSearchParams(params, { replace: true })
  }

  return [activeTab, setTab]
}
